/**
 * OmniLeagues - Public Manage Booking Page (requester self-service)
 * Route: /manage/:token
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CalendarClock, Loader2, XCircle } from 'lucide-react';
import { publicManage, publicCancel, formatInTz } from '../../components/booking/bookingApi';

export default function PublicManageBookingPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await publicManage(token);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onCancel = async () => {
    if (!window.confirm('Cancel this booking?')) return;
    setCancelling(true);
    try {
      await publicCancel(token);
      toast.success('Booking cancelled');
      load();
    } catch {
      toast.error('Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <header className="border-b border-slate-800/50 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-amber-500" />
          <span className="font-bold tracking-wide">OmniLeagues</span>
          <span className="text-xs text-slate-500 ml-2">Manage Booking</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center gap-2 py-24 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : !data ? (
          <div className="text-center py-24">
            <p className="text-xl font-semibold">Booking not found</p>
          </div>
        ) : (
          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-6 space-y-4">
              <div>
                <div className="text-xs uppercase text-slate-400 tracking-wider">
                  {data.org?.name}
                </div>
                <h1 className="text-2xl font-bold">{data.event_type?.name}</h1>
              </div>
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-slate-400">Time:</span>{' '}
                  {formatInTz(data.request.slot_start_utc, data.request.timezone)}
                </div>
                <div>
                  <span className="text-slate-400">Team:</span> {data.request.requester.team_name}
                </div>
                <div>
                  <span className="text-slate-400">Status:</span>{' '}
                  <span
                    className={
                      data.request.status === 'approved'
                        ? 'text-emerald-400'
                        : data.request.status === 'pending'
                        ? 'text-amber-400'
                        : 'text-red-400'
                    }
                  >
                    {data.request.status}
                  </span>
                </div>
                {data.request.decline_reason && (
                  <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-sm">
                    <strong>Reason:</strong> {data.request.decline_reason}
                  </div>
                )}
              </div>
              {data.request.status === 'pending' || data.request.status === 'approved' ? (
                <Button
                  variant="outline"
                  className="text-red-400 border-red-500/40"
                  onClick={onCancel}
                  disabled={cancelling}
                  data-testid="public-manage-cancel"
                >
                  <XCircle className="h-4 w-4 mr-1" /> Cancel Booking
                </Button>
              ) : null}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
